import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { handleInstall } from "@/lib/install";

export default function Nav() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const links = [
    { label: "Home", href: "/#top", route: false },
    { label: "Registry", href: "/registry", route: true },
    { label: "Docs", href: "/docs", route: false },
  ];

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
        className="fixed top-0 left-0 right-0 z-50 bg-transparent"
      >
        <div className="px-6 md:px-10 h-14 md:h-16 flex items-center justify-between">
          {/* Brand */}
          <a
            href="/#top"
            data-testid="brand-link"
            className="flex items-center gap-2 group"
          >
            <span className="relative inline-flex flex-col font-serif text-2xl leading-none tracking-tight">
              <span>Windie</span>
              <span
                aria-hidden="true"
                className="mt-1 h-[2px] w-8 bg-[hsl(38_92%_55%)]"
              />
            </span>
          </a>

          {/* Links */}
          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              l.route ? (
                <Link
                  key={l.href}
                  to={l.href}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-70 hover:text-ink transition-colors"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-70 hover:text-ink transition-colors"
                >
                  {l.label}
                </a>
              )
            ))}
            <a
              href="/#top"
              onClick={handleInstall}
              data-testid="nav-install-cta"
              className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-[color:var(--w-bg)] font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-cobalt transition-[background-color] duration-200"
            >
              Install
            </a>
          </nav>

          {/* Mobile menu */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden font-mono text-[11px] uppercase tracking-[0.18em]"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-navigation"
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? "Close" : "Menu"}
          </button>
        </div>
      </motion.header>

      {open ? (
        <div
          id="mobile-navigation"
          role="navigation"
          aria-label="Mobile navigation"
          className="fixed inset-x-0 top-14 bottom-0 z-40 overflow-y-auto border-t border-ink-10 bg-paper px-6 py-8 md:hidden"
        >
          <div className="flex min-h-full flex-col">
            <div className="flex flex-col">
              {links.map((l) => {
                const className =
                  "block border-b border-ink-10 py-7 font-serif text-5xl leading-none transition-colors hover:text-cobalt";

                return l.route ? (
                  <Link
                    key={l.href}
                    to={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                    className={className}
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                    className={className}
                  >
                    {l.label}
                  </a>
                );
              })}
            </div>

            <a
              href="/#top"
              onClick={(event) => {
                setOpen(false);
                handleInstall(event);
              }}
              data-testid="nav-mobile-install-cta"
              className="mt-8 inline-flex min-h-14 w-full items-center justify-center bg-ink px-5 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-[color:var(--w-bg)] hover:bg-cobalt transition-[background-color] duration-200"
            >
              Install Windie
            </a>
          </div>
        </div>
      ) : null}
    </>
  );
}
